import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MapPin,
  Bell,
  BarChart3,
  Zap,
  Brain,
  Shield,
  Lock,
  Settings,
  Syringe,
  BedDouble,
  Accessibility,
  Monitor,
  ShoppingCart,
  Cable,
  Package,
  Check,
  X,
  List,
  Clock,
  AlertCircle,
  Users,
  ClipboardCheck,
  Stethoscope,
  Building2,
} from "lucide-react";
import InstitutionalFooter from "@/components/InstitutionalFooter";
import FAQSection from "@/components/FAQSection";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LanguageProvider, useLanguage } from "@/i18n";
import PrivacyPolicy from "@/pages/PrivacyPolicy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component
const Logo = ({ light = false }) => (
  <div className="flex items-center gap-2">
    <div className="w-8 h-8 bg-[#8B1B3A] rounded flex items-center justify-center">
      <MapPin className="w-5 h-5 text-white" />
    </div>
    <span className={`text-xl font-semibold ${light ? 'text-white' : 'text-[#0F172A]'}`}>KlinikTrack</span>
  </div>
);

// Hero Section
const HeroSection = ({ onCtaClick }) => {
  const { t } = useLanguage();
  return (
  <section
    id="hero"
    className="relative min-h-[90vh] flex items-center overflow-hidden"
    data-testid="hero-section"
  >
    {/* Background Image */}
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_1f6b2256-14bd-4c5d-bb3f-c6ae5123a39f/artifacts/ntwkpdae_ChatGPT%20Image%20Feb%205%2C%202026%2C%2011_00_54%20AM.png')`
      }}
    />
    {/* Light overlay filter */}
    <div className="absolute inset-0 bg-white/55" />
    
    {/* Content */}
    <div className="container relative z-10">
      <div className="max-w-3xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-4">
          {t('hero.title')}
        </h1>
        <p className="text-lg sm:text-xl text-[#475569] font-medium mb-4">
          {t('hero.subtitle')}
        </p>
        <p className="text-base text-[#64748B] mb-8 max-w-xl leading-relaxed">
          {t('hero.tagline1')}
          <br />
          {t('hero.tagline2')}
        </p>
        <Button
          onClick={onCtaClick}
          className="bg-[#8B1B3A] hover:bg-[#701530] text-white px-6 py-3 h-auto text-base rounded-md"
          data-testid="hero-cta-button"
        >
          {t('hero.cta')}
        </Button>
      </div>
    </div>
  </section>
);
};

// Problem Section
const ProblemSection = () => {
  const { t } = useLanguage();
  return (
  <section
    id="problem"
    className="section-dark section"
    data-testid="problem-section"
  >
    <div className="container">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left: Content */}
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            {t('problem.title')}
          </h2>
          <p className="text-[#CBD5E1] text-base sm:text-lg mb-8">
            {t('problem.description')}
          </p>
          <ul className="space-y-3 text-[#CBD5E1]">
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-[#EF4444] mt-0.5 flex-shrink-0" />
              <span>{t('problem.bullet1')}</span>
            </li>
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-[#EF4444] mt-0.5 flex-shrink-0" />
              <span>{t('problem.bullet2')}</span>
            </li>
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-[#EF4444] mt-0.5 flex-shrink-0" />
              <span>{t('problem.bullet3')}</span>
            </li>
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-[#EF4444] mt-0.5 flex-shrink-0" />
              <span>{t('problem.bullet4')}</span>
            </li>
          </ul>
        </div>
        
        {/* Right: Image */}
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_1f6b2256-14bd-4c5d-bb3f-c6ae5123a39f/artifacts/5turtnaq_shot-of-a-medical-practitioner-looking-stressed-in-2026-01-09-11-20-42-utc.jpg"
            alt="Medical practitioner looking stressed"
            className="w-full rounded-lg shadow-lg"
          />
        </div>
      </div>
    </div>
  </section>
);
};

// Solution Section
const SolutionSection = () => {
  const { t } = useLanguage();
  return (
  <section id="solution" className="section" data-testid="solution-section">
    <div className="container">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left: Content */}
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6">
            {t('solution.title')}
          </h2>
          <p className="text-[#475569] text-base sm:text-lg mb-8">
            {t('solution.description')}
          </p>
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="icon-box flex-shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#0F172A] mb-1">
                  {t('solution.feature1Title')}
                </h3>
                <p className="text-sm text-[#64748B]">
                  {t('solution.feature1Desc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="icon-box flex-shrink-0">
                <Bell className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#0F172A] mb-1">
                  {t('solution.feature2Title')}
                </h3>
                <p className="text-sm text-[#64748B]">
                  {t('solution.feature2Desc')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="icon-box flex-shrink-0">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#0F172A] mb-1">
                  {t('solution.feature3Title')}
                </h3>
                <p className="text-sm text-[#64748B]">
                  {t('solution.feature3Desc')}
                </p>
              </div>
            </div>
          </div>
          <p className="text-[#0F172A] font-medium text-lg">
            {t('solution.closing')}
          </p>
        </div>
        
        {/* Right: Illustration */}
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_1f6b2256-14bd-4c5d-bb3f-c6ae5123a39f/artifacts/gnxs7s4o_image.png"
            alt="Hospital Map - Asset tracking dashboard"
            className="w-full rounded-lg shadow-lg border border-[#E2E8F0]"
          />
        </div>
      </div>
    </div>
  </section>
);
};

// What to Track Section
const TrackSection = () => {
  const { t } = useLanguage();
  const trackItems = [
    { icon: Syringe, key: "infusionPumps" },
    { icon: BedDouble, key: "beds" },
    { icon: Accessibility, key: "wheelchairs" },
    { icon: Monitor, key: "biomedical" },
    { icon: ShoppingCart, key: "carts" },
    { icon: Cable, key: "cables" },
    { icon: Package, key: "medication" },
  ];

  return (
    <section
      id="track"
      className="section-surface section"
      data-testid="track-section"
    >
      <div className="container">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">
          {t('track.title')}
        </h2>
        <p className="text-[#475569] text-base sm:text-lg mb-8 max-w-2xl">
          {t('track.description')}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {trackItems.map((item, index) => (
            <div key={index} className="track-item">
              <item.icon className="track-item-icon" />
              <p className="text-sm font-medium text-[#0F172A]">{t(`track.items.${item.key}`)}</p>
            </div>
          ))}
        </div>
        <p className="text-[#64748B] text-sm mb-6">
          {t('track.moreText')}
        </p>
        <div className="disclaimer">
          <p className="font-medium">
            {t('track.disclaimer')}
          </p>
        </div>
      </div>
    </section>
  );
};

// Why KlinikTrack Section
const WhySection = () => {
  const { t } = useLanguage();
  const features = [
    { icon: Zap, key: "plugPlay" },
    { icon: Brain, key: "aiAssisted" },
    { icon: Shield, key: "resilient" },
    { icon: Lock, key: "gdpr" },
    { icon: Settings, key: "operational" },
  ];

  return (
    <section id="why" className="section" data-testid="why-section">
      <div className="container">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10">
          {t('why.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="feature-card p-6 rounded-lg">
              <div className="icon-box mb-4">
                <feature.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">
                {t(`why.${feature.key}.title`)}
              </h3>
              <p className="text-sm text-[#64748B] leading-relaxed">
                {t(`why.${feature.key}.desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Platform Preview Section
const PreviewSection = () => {
  const { t, language } = useLanguage();
  
  const dashboardImages = {
    en: "https://customer-assets.emergentagent.com/job_1f6b2256-14bd-4c5d-bb3f-c6ae5123a39f/artifacts/9zvnc2s6_image.png",
    de: "https://customer-assets.emergentagent.com/job_1f6b2256-14bd-4c5d-bb3f-c6ae5123a39f/artifacts/cel334i7_image.png"
  };
  
  return (
  <section id="preview" className="section" data-testid="preview-section">
    <div className="container">
      <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">
        {t('preview.title')}
      </h2>
      <p className="text-[#475569] text-base sm:text-lg mb-8 max-w-2xl">
        {t('preview.description')}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="flex items-center gap-3 text-[#475569]">
          <List className="w-5 h-5 text-[#8B1B3A]" />
          <span>{t('preview.feature1')}</span>
        </div>
        <div className="flex items-center gap-3 text-[#475569]">
          <Clock className="w-5 h-5 text-[#8B1B3A]" />
          <span>{t('preview.feature2')}</span>
        </div>
        <div className="flex items-center gap-3 text-[#475569]">
          <AlertCircle className="w-5 h-5 text-[#8B1B3A]" />
          <span>{t('preview.feature3')}</span>
        </div>
      </div>
      
      {/* Dashboard Image - different per language */}
      <div className="relative">
        <img 
          src={dashboardImages[language]}
          alt={language === 'de' ? 'KlinikTrack Vermögenswert-Inventar' : 'KlinikTrack Asset Inventory Dashboard'}
          className="w-full rounded-lg shadow-lg border border-[#E2E8F0]"
        />
      </div>
    </div>
  </section>
);
};

// Demo Form Section
const DemoFormSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    role: "",
    email: "",
    country: "",
    department: "",
    phone: "",
    facility_type: "",
    hospital_size: "",
    equipment_to_track: [],
    equipment_other: "",
    target_timeframe: "",
    comments: "",
    gdpr_consent: false,
    referral_source: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const countries = [
    { value: "germany", key: "germany" },
    { value: "austria", key: "austria" },
    { value: "switzerland", key: "switzerland" },
    { value: "luxembourg", key: "luxembourg" },
    { value: "liechtenstein", key: "liechtenstein" },
    { value: "other_europe", key: "otherEurope" },
  ];

  const departments = [
    { value: "operations", key: "operations" },
    { value: "logistics", key: "logistics" },
    { value: "biomedical", key: "biomedical" },
    { value: "it", key: "it" },
    { value: "quality_compliance", key: "qualityCompliance" },
    { value: "nursing", key: "nursing" },
    { value: "procurement", key: "procurement" },
    { value: "other", key: "other" },
  ];

  const facilityTypes = [
    { value: "public_hospital", key: "publicHospital" },
    { value: "private_clinic", key: "privateClinic" },
    { value: "university_hospital", key: "universityHospital" },
    { value: "rehab_longterm", key: "rehabLongterm" },
    { value: "other", key: "other" },
  ];

  const hospitalSizes = [
    { value: "under_200", key: "under200" },
    { value: "200_500", key: "from200to500" },
    { value: "500_1000", key: "from500to1000" },
    { value: "over_1000", key: "over1000" },
  ];

  const equipmentOptions = [
    { value: "infusion_pumps", key: "infusionPumps" },
    { value: "beds", key: "beds" },
    { value: "wheelchairs", key: "wheelchairs" },
    { value: "biomedical_mobile", key: "biomedicalMobile" },
    { value: "carts", key: "carts" },
    { value: "cables_accessories", key: "cablesAccessories" },
    { value: "medication_boxes", key: "medicationBoxes" },
  ];

  const timeframes = [
    { value: "0_30_days", key: "days030" },
    { value: "1_3_months", key: "months13" },
    { value: "3_6_months", key: "months36" },
    { value: "over_6_months", key: "over6months" },
    { value: "exploring", key: "exploring" },
  ];

  const referralSources = [
    { value: "referral", key: "referral" },
    { value: "linkedin", key: "linkedin" },
    { value: "event", key: "event" },
    { value: "google", key: "google" },
    { value: "partner", key: "partner" },
    { value: "other", key: "other" },
  ];

  const handleEquipmentChange = (value, checked) => {
    setFormData(prev => ({
      ...prev,
      equipment_to_track: checked
        ? [...prev.equipment_to_track, value]
        : prev.equipment_to_track.filter(item => item !== value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.organization || !formData.role || !formData.email || !formData.country) {
      toast.error(t('form.errorFillFields'));
      return;
    }

    if (!formData.gdpr_consent) {
      toast.error(t('form.errorGdpr'));
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/demo-requests`, { ...formData, language });
      setIsSubmitted(true);
      toast.success(t('form.successToast'));
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(t('form.errorSubmit'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section
        id="demo"
        className="section-surface section"
        data-testid="demo-section"
      >
        <div className="container">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-[#10B981] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
              {t('form.successTitle')}
            </h2>
            <p className="text-[#64748B]">
              {t('form.successMessage')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="demo"
      className="section-surface section"
      data-testid="demo-section"
    >
      <div className="container">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4 text-center">
            {t('form.title')}
          </h2>
          <p className="text-[#475569] text-center mb-8">
            {t('form.description')}
          </p>
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* A) Identity Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#0F172A] border-b border-[#E2E8F0] pb-2">
                {t('form.sections.yourInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.fullName')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-[#F8FAFC] border-[#E2E8F0]"
                    placeholder={t('form.placeholders.fullName')}
                    data-testid="form-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.email')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#F8FAFC] border-[#E2E8F0]"
                    placeholder={t('form.placeholders.email')}
                    data-testid="form-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="organization" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.organization')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="organization"
                    type="text"
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="bg-[#F8FAFC] border-[#E2E8F0]"
                    placeholder={t('form.placeholders.organization')}
                    data-testid="form-organization-input"
                  />
                </div>
                <div>
                  <Label htmlFor="role" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.role')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="role"
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="bg-[#F8FAFC] border-[#E2E8F0]"
                    placeholder={t('form.placeholders.role')}
                    data-testid="form-role-input"
                  />
                </div>
                <div>
                  <Label htmlFor="country" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.country')} <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => setFormData({ ...formData, country: value })}
                  >
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0]" data-testid="form-country-select">
                      <SelectValue placeholder={t('form.placeholders.country')} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((item) => (
                        <SelectItem key={item.value} value={item.value}>{t(`countries.${item.key}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="department" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.department')}
                  </Label>
                  <Select
                    value={formData.department}
                    onValueChange={(value) => setFormData({ ...formData, department: value })}
                  >
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0]" data-testid="form-department-select">
                      <SelectValue placeholder={t('form.placeholders.department')} />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((item) => (
                        <SelectItem key={item.value} value={item.value}>{t(`departments.${item.key}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                  {t('form.fields.phone')} <span className="text-[#94A3B8]">{t('form.optional')}</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-[#F8FAFC] border-[#E2E8F0] max-w-xs"
                  placeholder={t('form.placeholders.phone')}
                  data-testid="form-phone-input"
                />
              </div>
            </div>

            {/* C) Hospital Qualification */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#0F172A] border-b border-[#E2E8F0] pb-2">
                {t('form.sections.hospitalInfo')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="facility_type" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.facilityType')}
                  </Label>
                  <Select
                    value={formData.facility_type}
                    onValueChange={(value) => setFormData({ ...formData, facility_type: value })}
                  >
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0]" data-testid="form-facility-select">
                      <SelectValue placeholder={t('form.placeholders.facilityType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {facilityTypes.map((item) => (
                        <SelectItem key={item.value} value={item.value}>{t(`facilityTypes.${item.key}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="hospital_size" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                    {t('form.fields.hospitalSize')}
                  </Label>
                  <Select
                    value={formData.hospital_size}
                    onValueChange={(value) => setFormData({ ...formData, hospital_size: value })}
                  >
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0]" data-testid="form-size-select">
                      <SelectValue placeholder={t('form.placeholders.hospitalSize')} />
                    </SelectTrigger>
                    <SelectContent>
                      {hospitalSizes.map((item) => (
                        <SelectItem key={item.value} value={item.value}>{t(`hospitalSizes.${item.key}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* D) What to Track */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#0F172A] border-b border-[#E2E8F0] pb-2">
                {t('form.sections.whatToTrack')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {equipmentOptions.map((item) => (
                  <div key={item.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={item.value}
                      checked={formData.equipment_to_track.includes(item.value)}
                      onCheckedChange={(checked) => handleEquipmentChange(item.value, checked)}
                      data-testid={`form-equipment-${item.value}`}
                    />
                    <Label htmlFor={item.value} className="text-sm text-[#475569] cursor-pointer">
                      {t(`equipment.${item.key}`)}
                    </Label>
                  </div>
                ))}
              </div>
              <div>
                <Label htmlFor="equipment_other" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                  {t('form.fields.otherEquipment')} <span className="text-[#94A3B8]">{t('form.pleaseSpecify')}</span>
                </Label>
                <Input
                  id="equipment_other"
                  type="text"
                  value={formData.equipment_other}
                  onChange={(e) => setFormData({ ...formData, equipment_other: e.target.value })}
                  className="bg-[#F8FAFC] border-[#E2E8F0]"
                  placeholder={t('form.placeholders.otherEquipment')}
                  data-testid="form-equipment-other"
                />
              </div>
            </div>

            {/* E) Timeline */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#0F172A] border-b border-[#E2E8F0] pb-2">
                {t('form.sections.timeline')}
              </h3>
              <div>
                <Label htmlFor="target_timeframe" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                  {t('form.fields.targetTimeframe')}
                </Label>
                <Select
                  value={formData.target_timeframe}
                  onValueChange={(value) => setFormData({ ...formData, target_timeframe: value })}
                >
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] max-w-xs" data-testid="form-timeframe-select">
                    <SelectValue placeholder={t('form.placeholders.timeframe')} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeframes.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{t(`timeframes.${item.key}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* F) Comments */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-[#0F172A] border-b border-[#E2E8F0] pb-2">
                {t('form.sections.additional')}
              </h3>
              <div>
                <Label htmlFor="referral_source" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                  {t('form.fields.referralSource')}
                </Label>
                <Select
                  value={formData.referral_source}
                  onValueChange={(value) => setFormData({ ...formData, referral_source: value })}
                >
                  <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0] max-w-xs" data-testid="form-referral-select">
                    <SelectValue placeholder={t('form.placeholders.referral')} />
                  </SelectTrigger>
                  <SelectContent>
                    {referralSources.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{t(`referralSources.${item.key}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="comments" className="text-sm font-medium text-[#0F172A] mb-1.5 block">
                  {t('form.fields.comments')} <span className="text-[#94A3B8]">{t('form.optional')}</span>
                </Label>
                <Textarea
                  id="comments"
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  className="bg-[#F8FAFC] border-[#E2E8F0] min-h-[100px]"
                  placeholder={t('form.placeholders.comments')}
                  data-testid="form-comments-textarea"
                />
              </div>
            </div>

            {/* G) GDPR Consent */}
            <div className="space-y-4 bg-[#F1F5F9] p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="gdpr_consent"
                  checked={formData.gdpr_consent}
                  onCheckedChange={(checked) => setFormData({ ...formData, gdpr_consent: checked })}
                  className="mt-1"
                  data-testid="form-gdpr-checkbox"
                />
                <Label htmlFor="gdpr_consent" className="text-sm text-[#475569] cursor-pointer leading-relaxed">
                  {t('form.gdprConsent')}
                  <span className="text-red-500"> *</span>
                  <br />
                  <Link to="/privacy" className="text-[#8B1B3A] hover:underline" target="_blank">{t('form.privacyPolicy')}</Link>
                </Label>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#8B1B3A] hover:bg-[#701530] text-white py-3 h-auto text-base rounded-md"
                data-testid="form-submit-button"
              >
                {isSubmitting ? t('form.submitting') : t('form.submit')}
              </Button>
              <p className="text-xs text-[#94A3B8] text-center mt-3">
                {t('form.reviewNote')}
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

// For Who Section
const AudienceSection = () => {
  const { t } = useLanguage();
  const audiences = [
    { icon: Building2, key: "hospitalManagement" },
    { icon: Users, key: "logistics" },
    { icon: Stethoscope, key: "biomedical" },
    { icon: ClipboardCheck, key: "quality" },
  ];

  return (
    <section id="audience" className="section" data-testid="audience-section">
      <div className="container">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-10 text-center">
          {t('audience.title')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {audiences.map((audience, index) => (
            <div key={index} className="text-center">
              <div className="icon-box mx-auto mb-3">
                <audience.icon className="w-6 h-6" />
              </div>
              <p className="text-sm font-medium text-[#0F172A]">{t(`audience.${audience.key}`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Navigation
// Navigation with scroll-aware transparency
const Navigation = ({ onCtaClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-sm border-b border-[#E2E8F0]' 
          : 'bg-transparent'
      }`}
    >
      <div className="container flex items-center justify-between h-16">
        <Link to="/">
          <Logo light={!scrolled} />
        </Link>
        <div className="flex items-center gap-4">
          <LanguageSwitcher light={!scrolled} />
          <Button
            onClick={onCtaClick}
            variant="outline"
            className={`text-sm transition-all duration-300 ${
              scrolled 
                ? 'border-[#8B1B3A] text-[#8B1B3A] hover:bg-[#8B1B3A] hover:text-white' 
                : 'border-white text-white hover:bg-white hover:text-[#8B1B3A]'
            }`}
            data-testid="nav-cta-button"
          >
            {t('nav.requestDemo')}
          </Button>
        </div>
      </div>
    </nav>
  );
};

// Landing Page Component
const LandingPage = () => {
  const scrollToDemo = () => {
    const demoSection = document.getElementById("demo");
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <Navigation onCtaClick={scrollToDemo} />
      <main>
        <HeroSection onCtaClick={scrollToDemo} />
        <ProblemSection />
        <SolutionSection />
        <TrackSection />
        <WhySection />
        <PreviewSection />
        <DemoFormSection />
        <AudienceSection />
        <FAQSection onCtaClick={scrollToDemo} />
      </main>
      <InstitutionalFooter />
    </>
  );
};

// Main App Component
function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <div className="App">
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<><PrivacyPolicy /><InstitutionalFooter /></>} />
          </Routes>
        </div>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
